import { StorageSchemas, type StorageKey, type StorageValue } from './schemas'

// Overload: No default → nullable
export function getStorage<K extends StorageKey>(key: K): StorageValue<K> | null
// Overload: With default → non-nullable
export function getStorage<K extends StorageKey>(
	key: K,
	defaultValue: StorageValue<K>,
): StorageValue<K>
/**
 * Type-safe localStorage getter with Zod validation
 */
export function getStorage<K extends StorageKey>(
	key: K,
	defaultValue?: StorageValue<K>,
): StorageValue<K> | null {
	try {
		const raw = localStorage.getItem(key)
		if (raw === null) {
			return defaultValue ?? null
		}

		const parsed = JSON.parse(raw)
		const schema = StorageSchemas[key]
		const result = schema.safeParse(parsed)

		if (result.success) {
			return result.data as StorageValue<K>
		}

		console.warn(
			`[Storage] Validation failed for "${key}":`,
			result.error.message,
		)
		return defaultValue ?? null
	} catch (error) {
		console.warn(`[Storage] Failed to read "${key}":`, error)
		return defaultValue ?? null
	}
}

/**
 * Type-safe localStorage setter with Zod validation
 */
export function setStorage<K extends StorageKey>(
	key: K,
	value: StorageValue<K>,
): boolean {
	try {
		const schema = StorageSchemas[key]
		const result = schema.safeParse(value)

		if (!result.success) {
			console.error(
				`[Storage] Invalid value for "${key}":`,
				result.error.message,
			)
			return false
		}

		localStorage.setItem(key, JSON.stringify(result.data))
		return true
	} catch (error) {
		console.error(`[Storage] Failed to write "${key}":`, error)
		return false
	}
}

/**
 * Update specific fields without replacing entire object
 */
export function updateStorage<K extends StorageKey>(
	key: K,
	update: Partial<StorageValue<K>>,
): boolean {
	try {
		const raw = localStorage.getItem(key)
		const existing = raw ? JSON.parse(raw) : {}
		const merged = { ...existing, ...update }

		const schema = StorageSchemas[key]
		const result = schema.safeParse(merged)

		if (!result.success) {
			console.error(
				`[Storage] Invalid update for "${key}":`,
				result.error.message,
			)
			return false
		}

		localStorage.setItem(key, JSON.stringify(result.data))
		return true
	} catch (error) {
		console.error(`[Storage] Failed to update "${key}":`, error)
		return false
	}
}

/**
 * Type-safe localStorage remover
 */
export function removeStorage<K extends StorageKey>(key: K): void {
	localStorage.removeItem(key)
}

/**
 * Check if a storage key exists
 */
export function hasStorage<K extends StorageKey>(key: K): boolean {
	return localStorage.getItem(key) !== null
}
